import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';

interface UseRealtimeUpdatesProps {
  onVideoUpdate?: () => void;
  onCreatorUpdate?: () => void;
  enablePolling?: boolean;
  pollingInterval?: number; // en minutes
}

export const useRealtimeUpdates = ({
  onVideoUpdate,
  onCreatorUpdate,
  enablePolling = true,
  pollingInterval = 30 // 30 minutes par défaut
}: UseRealtimeUpdatesProps = {}) => {
  const { user } = useAuth();
  const pollingIntervalRef = useRef<NodeJS.Timeout>();
  const lastUpdateRef = useRef<Date>(new Date());

  // Fonction pour vérifier les vidéos supprimées
  const checkForDeletedVideos = async () => {
    if (!user) return;

    try {
      // Récupérer les vidéos en cache
      const { data: cachedVideos } = await supabase
        .from('cached_videos_feed')
        .select('video_id, title, channel_name')
        .order('created_at', { ascending: false })
        .limit(100);

      if (!cachedVideos || cachedVideos.length === 0) return;

      // Vérifier un échantillon de vidéos (pour éviter de dépasser les quotas)
      const sampleVideos = cachedVideos.slice(0, 10);
      let deletedCount = 0;

      for (const video of sampleVideos) {
        try {
          // Vérifier si la vidéo existe encore
          const response = await fetch(`https://img.youtube.com/vi/${video.video_id}/maxresdefault.jpg`);
          
          if (!response.ok || response.status === 404) {
            // Vidéo probablement supprimée
            await supabase
              .from('cached_videos_feed')
              .delete()
              .eq('video_id', video.video_id);
            
            deletedCount++;
            console.log(`Vidéo supprimée détectée: ${video.title}`);
          }
        } catch (error) {
          console.error(`Erreur lors de la vérification de la vidéo ${video.video_id}:`, error);
        }
      }

      if (deletedCount > 0) {
        toast.info(`${deletedCount} vidéo(s) supprimée(s) détectée(s) et retirée(s) du cache`);
        onVideoUpdate?.();
      }
    } catch (error) {
      console.error('Erreur lors de la vérification des vidéos supprimées:', error);
    }
  };

  // Fonction pour mettre à jour les avatars des créateurs
  const updateCreatorAvatars = async () => {
    if (!user) return;

    try {
      // Récupérer les créateurs qui n'ont pas été mis à jour récemment
      const { data: creators } = await supabase
        .from('creators')
        .select('creator_id, name, avatar, created_at')
        .lt('created_at', new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()) // Plus de 7 jours
        .limit(5); // Limiter pour éviter les quotas

      if (!creators || creators.length === 0) return;

      // Récupérer la clé API de l'utilisateur
      const { data: profile } = await supabase
        .from('profiles')
        .select('youtube_api_key')
        .eq('id', user.id)
        .single();

      if (!profile?.youtube_api_key) return;

      let updatedCount = 0;

      for (const creator of creators) {
        try {
          const response = await fetch(
            `https://www.googleapis.com/youtube/v3/channels?part=snippet&id=${creator.creator_id}&key=${profile.youtube_api_key}`
          );

          if (response.ok) {
            const data = await response.json();
            if (data.items && data.items.length > 0) {
              const channelData = data.items[0];
              const newAvatar = channelData.snippet.thumbnails.medium?.url || channelData.snippet.thumbnails.default?.url;
              const newName = channelData.snippet.title;

              // Mettre à jour seulement si il y a des changements
              if (newAvatar !== creator.avatar || newName !== creator.name) {
                await supabase
                  .from('creators')
                  .update({
                    name: newName,
                    avatar: newAvatar,
                    created_at: new Date().toISOString() // Marquer comme récemment mis à jour
                  })
                  .eq('creator_id', creator.creator_id);

                updatedCount++;
                console.log(`Créateur mis à jour: ${newName}`);
              }
            }
          }
        } catch (error) {
          console.error(`Erreur lors de la mise à jour du créateur ${creator.creator_id}:`, error);
        }
      }

      if (updatedCount > 0) {
        toast.info(`${updatedCount} créateur(s) mis à jour`);
        onCreatorUpdate?.();
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour des créateurs:', error);
    }
  };

  // Fonction principale de polling
  const performPeriodicUpdate = async () => {
    console.log('🔄 Démarrage de la mise à jour périodique...');
    
    const now = new Date();
    const timeSinceLastUpdate = now.getTime() - lastUpdateRef.current.getTime();
    const minInterval = 15 * 60 * 1000; // 15 minutes minimum
    
    if (timeSinceLastUpdate < minInterval) {
      console.log('⏳ Mise à jour trop récente, passage ignoré');
      return;
    }

    lastUpdateRef.current = now;

    try {
      // Exécuter les vérifications en parallèle
      await Promise.allSettled([
        checkForDeletedVideos(),
        updateCreatorAvatars()
      ]);
      
      console.log('✅ Mise à jour périodique terminée');
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour périodique:', error);
    }
  };

  // Démarrer le polling
  useEffect(() => {
    if (!enablePolling || !user) return;

    // Première exécution après 5 minutes (délai initial)
    const initialTimeout = setTimeout(() => {
      performPeriodicUpdate();
    }, 5 * 60 * 1000);

    // Ensuite exécution à intervalles réguliers
    pollingIntervalRef.current = setInterval(() => {
      performPeriodicUpdate();
    }, pollingInterval * 60 * 1000);

    return () => {
      clearTimeout(initialTimeout);
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [user, enablePolling, pollingInterval]);

  // Écouter les changements en temps réel de Supabase
  useEffect(() => {
    if (!user) return;

    // Écouter les mises à jour des vidéos
    const videosChannel = supabase
      .channel('videos-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'cached_videos_feed'
        },
        (payload) => {
          console.log('🔄 Changement détecté dans cached_videos_feed:', payload);
          onVideoUpdate?.();
        }
      )
      .subscribe();

    // Écouter les mises à jour des créateurs
    const creatorsChannel = supabase
      .channel('creators-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'creators'
        },
        (payload) => {
          console.log('🔄 Changement détecté dans creators:', payload);
          onCreatorUpdate?.();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(videosChannel);
      supabase.removeChannel(creatorsChannel);
    };
  }, [user, onVideoUpdate, onCreatorUpdate]);

  return {
    triggerManualUpdate: performPeriodicUpdate,
    lastUpdate: lastUpdateRef.current
  };
};